import { useState, useRef, useCallback, useEffect } from 'react'
import { WEBSOCKET_ENDPOINTS } from '../contracts/endpoints'
import { API_BASE_URL } from '../services/api'
import { resolveWebSocketUrl } from '../services/urls'
import type { ConversationWsMessage, FnaFormData } from '../types'
import {
  beginIntentionalClose,
  clearSocketReference,
  getPreOpenRejectionMessage,
  getWebSocketCloseError,
  markWebSocketOpened,
  prepareWebSocketConnect,
  registerSlotReleaseOnPageHide,
  scheduleReconnect,
  startKeepAlive,
  stopKeepAlive,
} from './websocketLifecycle'

const CONVERSATION_WS_OVERRIDE = import.meta.env.VITE_CONVERSATION_WS_URL

interface ConversationMessage {
  role: 'user' | 'assistant'
  content: string
  audio?: string
}

interface UseConversationWebSocketReturn {
  isConnected: boolean
  messages: ConversationMessage[]
  formData: FnaFormData
  isComplete: boolean
  error: string | null
  sendTextMessage: (text: string) => void
  sendAudioData: (audioBlob: Blob) => void
  disconnect: () => void
  reconnect: () => void
}

/**
 * Custom hook for managing WebSocket connection to the guided conversation consumer.
 * Connects to /ws/conversation/ and handles the structured FNA data collection flow.
 *
 * @returns Object with connection state, messages, form data, and control functions
 */
export const useConversationWebSocket = (): UseConversationWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [formData, setFormData] = useState<FnaFormData>({})
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)
  const intentionalCloseRef = useRef(false)
  const isMountedRef = useRef(false)
  const keepAliveIntervalRef = useRef<number | null>(null)
  const handleMessageRef = useRef<(data: ConversationWsMessage) => void>(() => {})
  const isCompleteRef = useRef(false)

  /**
   * Merges incoming form_data fields into accumulated state.
   * Mirrors the merge logic from Django's conversation.html template.
   */
  const mergeFormData = useCallback((incoming: FnaFormData) => {
    setFormData(prev => {
      const merged = { ...prev }

      for (const [key, value] of Object.entries(incoming)) {
        if (value === null || value === undefined) continue
        if (typeof value === 'string' && value.trim() === '') continue

        if (Array.isArray(value)) {
          merged[key] = value
        } else if (typeof value === 'string') {
          merged[key] = value.trim()
        } else {
          merged[key] = value
        }
      }

      try {
        localStorage.setItem('form_data', JSON.stringify(merged))
      } catch (err) {
        console.error({
          message: 'Error persisting form_data in conversation WebSocket',
          error: err instanceof Error ? err.message : String(err),
          context: { action: 'mergeFormData' },
        })
      }

      return merged
    })
  }, [])

  /**
   * Handles incoming WebSocket messages from the ConversationConsumer.
   */
  const handleMessage = useCallback((data: ConversationWsMessage) => {
    if (data.error) {
      setError(data.error)
      return
    }

    if (data.warning) {
      console.warn({ message: 'Conversation WebSocket warning', context: { warning: data.warning } })
      return
    }

    // Merge form data if present
    if (data.form_data) {
      mergeFormData(data.form_data)
    }

    // Handle empty response (server error recovery)
    if (data.response !== undefined && data.response === '') {
      console.warn({
        message: 'Received empty response from conversation server',
        context: { hasFormData: !!data.form_data },
      })
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant' as const,
          content: 'I had trouble processing that. Could you please try again?',
        },
      ])
      return
    }

    // Add assistant response to messages
    if (data.response) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant' as const,
          content: data.response!,
          audio: data.audio,
        },
      ])
    }

    // Check for redirect signal (conversation complete)
    if (data.redirect) {
      setIsComplete(true)
    }
  }, [mergeFormData])

  // Keep refs in sync so connect() always uses the latest callbacks
  useEffect(() => {
    handleMessageRef.current = handleMessage
  }, [handleMessage])

  useEffect(() => {
    isCompleteRef.current = isComplete
  }, [isComplete])

  /**
   * Establishes WebSocket connection to the conversation consumer.
   */
  const connect = useCallback(() => {
    try {
      if (!prepareWebSocketConnect({
        socketRef,
        reconnectTimeoutRef,
        reconnectAttemptRef,
        intentionalCloseRef,
        isMountedRef,
      })) {
        return
      }

      const wsUrl = resolveWebSocketUrl(WEBSOCKET_ENDPOINTS.conversation, {
        baseUrl: API_BASE_URL,
        overrideUrl: CONVERSATION_WS_OVERRIDE,
      })

      const socket = new WebSocket(wsUrl)
      let socketOpened = false
      socketRef.current = socket
      setError(null)

      socket.onopen = () => {
        if (socketRef.current !== socket) {
          return
        }

        socketOpened = true
        markWebSocketOpened(reconnectAttemptRef, reconnectTimeoutRef)
        stopKeepAlive(keepAliveIntervalRef)
        keepAliveIntervalRef.current = startKeepAlive(socketRef)
        console.info({ message: 'Conversation WebSocket connected', context: {} })
        setIsConnected(true)
        setError(null)
      }

      socket.onmessage = (event) => {
        if (socketRef.current !== socket) {
          return
        }

        try {
          const data = JSON.parse(event.data) as ConversationWsMessage
          if ((data as { type?: string }).type === 'pong') {
            return
          }
          handleMessageRef.current(data)
        } catch (err) {
          console.error({
            message: 'Error parsing conversation WebSocket message',
            error: err instanceof Error ? err.message : String(err),
            context: { rawData: event.data },
          })
        }
      }

      socket.onclose = (event) => {
        stopKeepAlive(keepAliveIntervalRef)
        clearSocketReference(socketRef, socket)
        if (socketRef.current && socketRef.current !== socket) {
          return
        }
        console.info({ message: 'Conversation WebSocket closed', context: {} })

        if (!isMountedRef.current) {
          return
        }

        setIsConnected(false)
        const closeError = getWebSocketCloseError(event.code, event.reason)
        if (closeError) {
          setError(closeError)
          return
        }

        if (!socketOpened) {
          setError(getPreOpenRejectionMessage('conversation connection'))
          return
        }

        if (!isCompleteRef.current) {
          scheduleReconnect({
            socketRef,
            reconnectTimeoutRef,
            reconnectAttemptRef,
            intentionalCloseRef,
            isMountedRef,
          }, connect)
        }
      }

      socket.onerror = (err) => {
        if (socketRef.current !== socket) {
          return
        }

        console.error({
          message: 'Conversation WebSocket error',
          context: { error: err },
        })
        setError('Connection error. Please check your connection.')
      }
    } catch (err) {
      console.error({
        message: 'Error establishing conversation WebSocket',
        error: err instanceof Error ? err.message : String(err),
        context: {},
      })
    }
  }, [])

  /**
   * Sends a text message through the WebSocket.
   *
   * @param text - The message text to send
   */
  const sendTextMessage = useCallback((text: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected. Please wait for reconnection.')
      return
    }

    try {
      // Add user message to display
      setMessages(prev => [
        ...prev,
        { role: 'user' as const, content: text },
      ])

      socketRef.current.send(JSON.stringify({ message: text }))
    } catch (err) {
      console.error({
        message: 'Error sending text message',
        error: err instanceof Error ? err.message : String(err),
        context: { action: 'sendTextMessage' },
      })
      setError('Failed to send message.')
    }
  }, [])

  /**
   * Sends audio data through the WebSocket as raw bytes.
   *
   * @param audioBlob - The recorded audio blob
   */
  const sendAudioData = useCallback((audioBlob: Blob) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected. Please wait for reconnection.')
      return
    }

    try {
      // Add placeholder user message
      setMessages(prev => [
        ...prev,
        { role: 'user' as const, content: '[Voice message]' },
      ])

      audioBlob.arrayBuffer().then(buffer => {
        socketRef.current?.send(buffer)
      }).catch(err => {
        console.error({
          message: 'Error converting audio to ArrayBuffer',
          error: err instanceof Error ? err.message : String(err),
          context: { action: 'sendAudioData' },
        })
        setError('Failed to send audio.')
      })
    } catch (err) {
      console.error({
        message: 'Error sending audio data',
        error: err instanceof Error ? err.message : String(err),
        context: { action: 'sendAudioData' },
      })
      setError('Failed to send audio.')
    }
  }, [])

  /**
   * Disconnects the managed WebSocket and suppresses automatic reconnect.
   *
   * @param resetState - When true, clears user-facing connection state immediately.
   */
  const disconnectSocket = useCallback((resetState: boolean) => {
    beginIntentionalClose(intentionalCloseRef, reconnectTimeoutRef)
    stopKeepAlive(keepAliveIntervalRef)

    const socket = socketRef.current
    socketRef.current = null
    socket?.close()

    if (!resetState || !isMountedRef.current) {
      return
    }

    setIsConnected(false)
    setError(null)
  }, [])

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    connect()
    const unregisterPageHide = registerSlotReleaseOnPageHide(socketRef)

    return () => {
      isMountedRef.current = false
      unregisterPageHide()
      disconnectSocket(false)
    }
  }, [connect, disconnectSocket])

  /**
   * Disconnect the socket and clear the connection error state.
   *
   * @returns void
   */
  const disconnect = useCallback(() => {
    disconnectSocket(true)
  }, [disconnectSocket])

  return {
    isConnected,
    messages,
    formData,
    isComplete,
    error,
    sendTextMessage,
    sendAudioData,
    disconnect,
    reconnect: connect,
  }
}
